import React, { useState, useEffect } from 'react';
import { ConfigProvider, Modal, Segmented } from 'antd';
import { useNavigate } from 'react-router';
import LndButton from '@components/LndButton';
import BillingService, { isBillingSessionExpiredError } from '@apis/BillingService';
import { Box, Briefcase, Warning2 } from 'iconsax-react';
import AnimatedArrowRight from '@components/icons/AnimatedArrowRight';
import { useAuth } from '@contexts/AuthContext';
import { UserRole } from '@enums/Permissions';
import posthog from '@libs/posthog';
import { EVENTS } from '@lib/analytics';
import { getPromoterCode } from '@utils/promoterCode';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import type { PlanPrice } from '@customTypes/billing';

interface SubscriptionRequiredModalProps {
  open: boolean;
  onClose: () => void;
}

interface Feature {
  text: string;
  included: boolean;
}

const CheckIcon = () => (
  <svg
    className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-600"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const DashIcon = () => (
  <span className="w-4 mt-0.5 flex-shrink-0 text-center text-neutral-200 font-medium">
    &ndash;
  </span>
);

const defaultPlanFeatures: Record<string, Feature[]> = {
  starter: [
    { text: 'Performance reporting', included: true },
    { text: 'Competitor audits', included: true },
    { text: 'Account insights & recommendations', included: true },
    { text: 'Autonomous campaign execution', included: false },
    { text: 'Continuous optimization', included: false },
  ],
  business: [
    { text: 'Performance reporting', included: true },
    { text: 'Competitor audits', included: true },
    { text: 'Account insights & recommendations', included: true },
    { text: 'Autonomous campaign execution', included: true },
    { text: 'Continuous optimization', included: true },
  ],
};

const defaultPlanDescriptions: Record<string, string> = {
  starter: 'Reporting, audits, and insights across your ad accounts.',
  business: 'Full autonomous campaign creation, management, and optimization.',
};

const defaultPlanSpendLimits: Record<string, string> = {
  starter: 'up to $10k ad spend',
  business: 'up to $50k ad spend',
};

const defaultPlanDisplayNames: Record<string, string> = {
  starter: 'Starter',
  business: 'Pro',
};

const planIcons: Record<string, React.ReactNode> = {
  starter: <Box size={28} color="currentColor" variant="Outline" />,
  business: <Briefcase size={28} color="currentColor" variant="Outline" />,
};

const SubscriptionRequiredModal: React.FC<SubscriptionRequiredModalProps> = ({
  open,
  onClose,
}) => {
  const { currentUser } = useAuth();
  const canManageBilling = currentUser?.user_type === UserRole.ADMIN;
  const hasSubscription = !!currentUser?.subscription;
  // Expired legacy trial: account older than 14 days with no subscription
  const accountAge = currentUser?.account_timestamp || currentUser?.timestamp;
  const isExpiredTrial = !hasSubscription && accountAge &&
    (Date.now() - new Date(accountAge).getTime()) > 14 * 24 * 60 * 60 * 1000;
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanPrice[]>([]);
  const [selectedTier, setSelectedTier] = useState<string>('business');
  const promoterCode = getPromoterCode();
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  useEffect(() => {
    if (open && canManageBilling) {
      BillingService.getPrices()
        .then(({ plans }) => setPlans(plans))
        .catch(() => {});
    }
  }, [open, canManageBilling]);

  const handleGoToBilling = () => {
    onClose();
    navigate('/settings?tab=billing');
  };

  const handleSelectPlan = async (priceId?: string) => {
    if (!canManageBilling) return;

    setLoadingPlan(priceId || 'default');
    posthog.capture(EVENTS.BILLING.CHECKOUT_INITIATED, {
      source: 'subscription_required_modal',
      price_id: priceId,
    });
    BillingService.createCheckoutSession(priceId).then(({ url }) => {
      if (url) {
        window.location.href = url;
      } else {
        handleGoToBilling();
      }
    }).catch((err) => {
      console.error('Error creating checkout session:', err);
      if (isBillingSessionExpiredError(err)) {
        setError('Session expired. Redirecting to login...');
        window.location.href = '/';
        return;
      }
      setError('Failed to create checkout session.');
    }).finally(() => {
      setLoadingPlan(null);
    });
  };

  const resolvedPlans = plans.length > 0 ? plans : [
    { tier: 'starter', name: 'Starter', amount: 49, price_id: '', currency: 'USD', interval: 'month' },
    { tier: 'business', name: 'Pro', amount: 229, price_id: '', currency: 'USD', interval: 'month' },
  ];

  const renderPlanCard = (plan: PlanPrice) => {
    const features = defaultPlanFeatures[plan.tier] || defaultPlanFeatures.starter;
    const description = defaultPlanDescriptions[plan.tier] || '';
    const spendLimit = defaultPlanSpendLimits[plan.tier] || '';
    const displayName = defaultPlanDisplayNames[plan.tier] || plan.name;
    const highlighted = plan.tier === 'business';

    return (
      <div key={plan.tier} className="p-8 pt-6 flex flex-col">
        <div className="mb-3 text-gray-900">
          {planIcons[plan.tier] || <Box size={12} color="currentColor" variant="Outline" />}
        </div>
        <div className="mt-1">
          <span className="text-lg font-medium text-gray-900 mr-2">
            {displayName}
          </span>
          <span className="text-xl font-extrabold text-gray-900">
            ${plan.amount}
          </span>
          <span className="text-sm text-gray-500">
            /mo.
          </span>
        </div>
        <p className="mt-3 text-sm text-gray-500 leading-relaxed">
          {description}
        </p>
        <div className="pt-4">
          <LndButton
            variant={highlighted ? 'primary' : 'link'}
            className="w-full group"
            loading={loadingPlan === plan.tier}
            onClick={() => {
              setLoadingPlan(plan.tier);
              handleSelectPlan(plan.price_id || undefined);
            }}
          >
            <span className="inline-flex items-center gap-1">
              {isExpiredTrial ? `Get ${displayName} Plan` : 'Start Free Trial'}
              <AnimatedArrowRight />
            </span>
          </LndButton>
        </div>
        <div className="pt-4 mt-4 border-t border-neutral-100">
          <ul className="space-y-3 list-none p-0 m-0">
            {spendLimit && (
              <li className="flex items-start gap-3 text-sm">
                <CheckIcon />
                <span className="text-gray-900">{spendLimit}</span>
              </li>
            )}
            {features.map((f) => (
              <li key={f.text} className="flex items-start gap-3 text-sm">
                {f.included ? <CheckIcon /> : <DashIcon />}
                <span className={f.included ? 'text-gray-900' : 'text-neutral-300'}>
                  {f.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const segmentOptions = resolvedPlans.map((plan) => ({
    label: defaultPlanDisplayNames[plan.tier] || plan.name,
    value: plan.tier,
  }));

  const activeMobilePlan = resolvedPlans.find((p) => p.tier === selectedTier) || resolvedPlans[0];

  return (
    <Modal
      open={open}
      onCancel={hasSubscription ? onClose : undefined}
      footer={null}
      closable={false}
      closeIcon={null}
      maskClosable={hasSubscription}
      keyboard={hasSubscription}
      width={isMobile ? '100%' : 680}
      centered
    >
      <div className="flex flex-col">
        <div className="p-6 pb-4 text-center">
          <h2 className="text-2xl font-bold mb-1 text-gray-900">
            {isExpiredTrial ? 'Choose a Plan' : 'Start a Free Trial'}
          </h2>
          {canManageBilling ? (
            <p className="text-gray-500">
              {isExpiredTrial
                ? 'Select a plan to continue using Leo.'
                : 'Select a plan to start your 14-day free trial.'}
            </p>
          ) : (
            <p className="text-gray-500">
              The main user of this account needs to subscribe to proceed further.
            </p>
          )}
        </div>

        {canManageBilling ? (
          <div className="border-t border-neutral-100">
            {isMobile ? (
              <div className="flex flex-col">
                <div className="pt-4 px-6 subscription-plan-toggle">
                  <ConfigProvider theme={{
                    components: {
                      Segmented: {
                        trackBg: '#f0f0f0',
                        trackPadding: 4,
                        itemSelectedBg: '#ffffff',
                        boxShadowTertiary: '0 1px 3px rgba(0,0,0,0.08)',
                      },
                    },
                  }}>
                    <Segmented
                      block
                      value={selectedTier}
                      options={segmentOptions}
                      onChange={(value) => setSelectedTier(value as string)}
                    />
                  </ConfigProvider>
                </div>
                {renderPlanCard(activeMobilePlan)}
              </div>
            ) : (
              <div className="grid grid-cols-2 divide-x divide-neutral-100">
                {resolvedPlans.map((plan) => renderPlanCard(plan))}
              </div>
            )}
            {promoterCode && (
              <p className="text-xs text-gray-500 mt-2 opacity-50 text-center pb-4">
                Applying promoter code: <span className="font-medium">{promoterCode}</span>
              </p>
            )}
            {error && (
              <div className="flex items-center justify-center gap-2 pb-4">
                <Warning2 size={16} color="red" />
                <p className="text-red-500">{error}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 pb-6">
            <p className="text-gray-500 text-center mb-4">
              The main user of this account needs to subscribe to proceed further.
            </p>
            {hasSubscription && (
              <LndButton
                onClick={onClose}
                variant="link"
                className="flex-1 w-full"
              >
                Close
              </LndButton>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SubscriptionRequiredModal;
